import { Box, Grid, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faCircleCheck, faListCheck, faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardChip, StandardChipVariantType } from "../../../../../../shared/components/badges/StandardChip"
import { AgreementStatus } from "../SupplierAdminPOSTypes"
import { hasAdyenApplicationBegun, isReadyForHostedOnboarding, showHostedOnboardingButton } from "../acquiringLib"
import { useHostedOnboarding } from "../acquiringHooks"

const useStyles = makeStyles()((theme) => ({
    content: {
        gap: "2rem",
    },
    boxTitleWrap: {
        display: "flex",
        alignItems: "center",
        gap: "1.25rem",
    },
    boxTitle: {
        fontSize: "1.1rem",
        fontWeight: 600,
        color: theme.palette.tailwind.slate[600],
    },
    infoWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
    },
    infoBox: {
        display: "grid",
        gridTemplateColumns: "16px 1fr",
        alignItems: "center",
        gap: "10px",
        color: theme.palette.tailwind.slate[600],
    },
}))

function getChipVariant(status: AgreementStatus): StandardChipVariantType {
    switch (status) {
        case AgreementStatus.VALID:
            return "success"
        case AgreementStatus.REJECTED:
            return "red"
        default:
            return "yellow"
    }
}

type StatusItemProps = {
    status: AgreementStatus
    variant: "info" | "action" | "check"
}

const StatusItem = ({ status, variant }: StatusItemProps) => {
    const { classes } = useStyles()

    return (
        <Box className={classes.infoBox}>
            {variant === "info" && (
                <>
                    <FontAwesomeIcon icon={faCircleInfo} style={{ color: "#2196F3" }} />
                    <LocalizedEnum base="supplier-admin-pos-acquirer-status-info" enumValue={status}>
                        <span>{status}</span>
                    </LocalizedEnum>
                </>
            )}
            {variant === "action" && (
                <>
                    <FontAwesomeIcon icon={faListCheck} style={{ color: "#22C55E" }} />
                    <LocalizedEnum base="supplier-admin-pos-acquirer-status-action" enumValue={status}>
                        <span>{status}</span>
                    </LocalizedEnum>
                </>
            )}
            {variant === "check" && (
                <>
                    <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#22C55E" }} />
                    <LocalizedEnum base="supplier-admin-pos-acquirer-status-check" enumValue={status}>
                        <span>{status}</span>
                    </LocalizedEnum>
                </>
            )}
        </Box>
    )
}

type SupplierAcquiringAgreementStatusProps = Readonly<{
    agreementId: number
    status: AgreementStatus
}>

export function SupplierAcquiringAgreementStatus({ agreementId, status }: SupplierAcquiringAgreementStatusProps) {
    const { classes } = useStyles()
    const { handleHostedOnboarding } = useHostedOnboarding(agreementId)

    return (
        <>
            <Grid container direction="column" wrap="nowrap" className={classes.content}>
                <Box className={classes.boxTitleWrap}>
                    <LocalizedStrict id="supplier-admin-pos-acquirer-status-title">
                        <Typography variant="body1" className={classes.boxTitle}>
                            Status:
                        </Typography>
                    </LocalizedStrict>

                    <LocalizedEnum base="supplier-admin-pos-acquirer-status" enumValue={status}>
                        <StandardChip variant={getChipVariant(status)}>{status}</StandardChip>
                    </LocalizedEnum>
                </Box>
                <Box className={classes.infoWrap}>
                    <StatusItem status={status} variant="info" />
                    {status !== AgreementStatus.VALID && status !== AgreementStatus.REJECTED && (
                        <StatusItem status={status} variant="action" />
                    )}
                    {status === AgreementStatus.VALID && <StatusItem status={status} variant="check" />}
                </Box>
            </Grid>
            {showHostedOnboardingButton(status) && (
                <Grid item>
                    <LocalizedStrict
                        id={
                            hasAdyenApplicationBegun(status)
                                ? "supplier-admin-pos-acquirer-continue-application-button"
                                : "supplier-admin-pos-acquirer-start-application-button"
                        }
                    >
                        <StandardButton
                            size="medium"
                            fullWidth={false}
                            disabled={!isReadyForHostedOnboarding(status)}
                            onClick={handleHostedOnboarding}
                            endIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                            style={{
                                paddingLeft: "32px",
                                paddingRight: "32px",
                            }}
                        >
                            Continue the application at Adyen
                        </StandardButton>
                    </LocalizedStrict>
                </Grid>
            )}
        </>
    )
}
